/**
 * Project registry.
 *
 * Every `*.ts` file in this folder that default-exports a `Project` is picked up
 * automatically — there is no list to keep in sync. To add a project, copy
 * `_template.ts.example`, fill it in, and save it here.
 */
import type { Project, Tag } from '@/types/content'

const modules = import.meta.glob<{ default: Project }>('./*.ts', {
  eager: true,
})

function collect(): Project[] {
  const found: Project[] = []
  const seen = new Map<string, string>()

  for (const [path, mod] of Object.entries(modules)) {
    if (path.endsWith('/index.ts')) continue
    const project = mod.default
    if (!project) {
      if (import.meta.env.DEV) {
        console.warn(`[content] ${path} has no default export; skipping.`)
      }
      continue
    }
    const previous = seen.get(project.slug)
    if (previous) {
      throw new Error(
        `[content] Duplicate project slug "${project.slug}" in ${path} and ${previous}.`,
      )
    }
    seen.set(project.slug, path)
    found.push(project)
  }

  return found.sort((a, b) => {
    const byOrder = (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
    return byOrder !== 0 ? byOrder : a.title.localeCompare(b.title)
  })
}

export const projects: Project[] = collect()

export const projectsBySlug: ReadonlyMap<string, Project> = new Map(
  projects.map((project) => [project.slug, project]),
)

export function getProject(slug: string | undefined): Project | undefined {
  return slug ? projectsBySlug.get(slug) : undefined
}

/** Every tag actually in use, in the order tags are declared across projects. */
export const allTags: Tag[] = [...new Set(projects.flatMap((p) => p.tags))].sort()

/** Number of projects carrying each tag, for the filter bar counts. */
export const tagCounts: ReadonlyMap<Tag, number> = projects.reduce((acc, project) => {
  for (const tag of project.tags) acc.set(tag, (acc.get(tag) ?? 0) + 1)
  return acc
}, new Map<Tag, number>())
